import { Controller } from "@hotwired/stimulus"

// Renders a readable tree by generations (rows), using JSON from /tree_json.
// No D3. No layout math. No skinny vertical name stacks.
export default class extends Controller {
  static targets = ["canvas", "status"]
  static values = {
    url: String,
    depth: Number
  }

  connect() {
    this.load()
  }

  async load() {
  try {
    this.statusTarget.textContent = "Loading…"

    const resp = await fetch(this.urlValue, {
      headers: { "Accept": "application/json" }
    })

    if (!resp.ok) {
      const text = await resp.text()
      throw new Error(`HTTP ${resp.status}: ${text.slice(0, 200)}`)
    }

    const contentType = resp.headers.get("content-type") || ""
    if (!contentType.includes("application/json")) {
      const text = await resp.text()
      throw new Error(`Expected JSON but got ${contentType || "unknown"}: ${text.slice(0, 200)}`)
    }

    const data = await resp.json()
    this.render(data)
    this.statusTarget.textContent = "Loaded."
  } catch (e) {
    console.error(e)
    this.statusTarget.textContent = `Could not load tree JSON. (${e.message})`
  }
}


  render(data) {
    // Expect: { root: {...}, max_depth: n } or just root payload
    const root = data.root || data
    const maxDepth = data.max_depth || this.depthValue || 5

    // Build rows: depth => [nodes...]
    const rows = Array.from({ length: maxDepth + 1 }, () => [])
    const queue = [{ node: root, depth: 0 }]

    // Avoid loops
    const seen = new Set()

    while (queue.length > 0) {
      const { node, depth } = queue.shift()
      if (!node || depth > maxDepth) continue

      const key = `${node.id}:${depth}`
      if (seen.has(key)) continue
      seen.add(key)

      rows[depth].push(node)

      const father = node.parents?.father || null
      const mother = node.parents?.mother || null

      if (father) queue.push({ node: father, depth: depth + 1 })
      if (mother) queue.push({ node: mother, depth: depth + 1 })
    }

    // Render rows
    this.canvasTarget.innerHTML = ""
    rows.forEach((nodes, depth) => {
      if (!nodes.length) return

      const row = document.createElement("div")
      row.className = "tree-row"

      const header = document.createElement("div")
      header.className = "tree-row-header"
      header.textContent = this.generationLabel(depth)

      const list = document.createElement("div")
      list.className = "tree-row-cards"

      nodes.forEach((n) => list.appendChild(this.nodeCard(n, depth)))

      row.appendChild(header)
      row.appendChild(list)
      this.canvasTarget.appendChild(row)
    })
  }

  nodeCard(n, depth) {
    const card = document.createElement("div")
    card.className = "tree-card"

    const title = document.createElement("div")
    title.className = "tree-card-title"
    // title.textContent = n.display_name || `(Unnamed #${n.id})`
    title.textContent = n.display_name || "(Unnamed)"


    const meta = document.createElement("div")
    meta.className = "tree-card-meta"
    const bits = []
    if (n.birth_year) bits.push(`b. ${n.birth_year}`)
    if (n.death_year) bits.push(`d. ${n.death_year}`)
    meta.textContent = bits.join(" · ")

    const badges = document.createElement("div")
    badges.className = "tree-card-badges"

    badges.appendChild(this.badge(n.missing_father ? "Father missing" : "Father set", n.missing_father))
    badges.appendChild(this.badge(n.missing_mother ? "Mother missing" : "Mother set", n.missing_mother))

    if (depth === 0) {
      const me = document.createElement("span")
      me.className = "badge text-bg-primary"
      me.textContent = "Me"
      badges.appendChild(me)
    }

    card.appendChild(title)
    if (meta.textContent.length) card.appendChild(meta)
    card.appendChild(badges)

    return card
  }

  badge(text, isMissing) {
    const b = document.createElement("span")
    b.className = `badge ${isMissing ? "text-bg-warning text-dark" : "text-bg-success"}`
    b.textContent = text
    return b
  }

  generationLabel(depth) {
    if (depth === 0) return "Me"
    if (depth === 1) return "Parents"
    if (depth === 2) return "Grandparents"
    const greats = depth - 2
    return greats <= 2 ? `${"Great-".repeat(greats)}Grandparents` : `${greats}× Great-Grandparents`
  }
}
